import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { msUntilNextSwitch, parsePreference, resolveDark, type ThemePreference } from '@/utils/theme';
import { withViewTransition } from '@/utils/view-transition';

export type { ThemePreference };

function systemDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

/** Aplica el tema; si cambia solo (automatico), lo hace con un fundido suave. */
function applyTheme(preference: ThemePreference, animate = false) {
  const root = document.documentElement;
  const dark = resolveDark(preference, new Date(), systemDark());
  if (root.classList.contains('dark') === dark) return;
  if (animate) {
    root.style.setProperty('--vt-x', '50%');
    root.style.setProperty('--vt-y', '0px');
    root.style.setProperty('--vt-r', '150vmax');
    withViewTransition(() => root.classList.toggle('dark', dark));
  } else {
    root.classList.toggle('dark', dark);
  }
}

export type AccountTab = 'password' | 'security';

export const useUiStore = defineStore('ui', () => {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem('domika-theme');
  } catch {
    /* sin almacenamiento: tema automatico */
  }
  // Por defecto "automatico": claro de dia, oscuro desde las 7 p. m.
  const theme = ref<ThemePreference>(parsePreference(stored));
  const sidebarOpen = ref(localStorage.getItem('domika-sidebar-collapsed') !== 'true');
  const sidebarMobileOpen = ref(false);
  /** Pestaña abierta de "Mi cuenta" (null = cerrada). */
  const accountTab = ref<AccountTab | null>(null);

  applyTheme(theme.value);

  // En automatico se programa el proximo cambio (6:00 o 19:00). Al volver a la
  // pestana (o despertar el equipo, que congela los temporizadores) se revisa.
  let switchTimer: ReturnType<typeof setTimeout> | undefined;
  function scheduleAutoSwitch() {
    clearTimeout(switchTimer);
    if (theme.value !== 'auto') return;
    switchTimer = setTimeout(() => {
      applyTheme('auto', true);
      scheduleAutoSwitch();
    }, msUntilNextSwitch(new Date()) + 500);
  }
  scheduleAutoSwitch();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && theme.value === 'auto') {
        applyTheme('auto', true);
        scheduleAutoSwitch();
      }
    });
  }
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (theme.value === 'system') applyTheme('system', true);
  });

  watch(theme, (value) => {
    try {
      localStorage.setItem('domika-theme', value);
    } catch {
      /* sin almacenamiento: solo esta sesion */
    }
    applyTheme(value);
    scheduleAutoSwitch();
  });

  watch(sidebarOpen, (value) => {
    localStorage.setItem('domika-sidebar-collapsed', String(!value));
  });

  function setTheme(value: ThemePreference) {
    theme.value = value;
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value;
  }

  function toggleMobileSidebar(value?: boolean) {
    sidebarMobileOpen.value = value ?? !sidebarMobileOpen.value;
  }

  function openAccount(tab: AccountTab = 'password') {
    accountTab.value = tab;
  }

  return {
    theme,
    setTheme,
    sidebarOpen,
    sidebarMobileOpen,
    toggleSidebar,
    toggleMobileSidebar,
    accountTab,
    openAccount,
  };
});

export interface Toast {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  /** Milisegundos que dura en pantalla (para la barra de tiempo). */
  duration: number;
}

let toastId = 0;

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([]);

  function push(type: Toast['type'], message: string, timeoutMs = 5000) {
    const id = ++toastId;
    toasts.value.push({ id, type, message, duration: timeoutMs });
    setTimeout(() => dismiss(id), timeoutMs);
  }

  function dismiss(id: number) {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }

  return {
    toasts,
    success: (msg: string) => push('success', msg),
    error: (msg: string) => push('error', msg),
    info: (msg: string) => push('info', msg),
    warning: (msg: string) => push('warning', msg),
    dismiss,
  };
});
